import { FunctionComponent, useState, useEffect } from 'react';
import Tab from './Tab/Tab';

import styles from './Tabs.module.scss';

export type TabsProps = {
  variant: 'plain' | 'standard' | 'chart' | 'color' | 'button' | 'dynamic' | 'highlight';
  index?: number | undefined;
  activeIndex?: number;
  color?: string;
  tabs: {
    label: string;
    icon?: React.JSX.Element;
    isDisabled?: boolean;
    isLocked?: boolean;
    tooltip?: React.JSX.Element | null;
    onClick?: () => void;
  }[];
};

type FC = FunctionComponent<TabsProps>;

const Tabs: FC = ({ variant, index, activeIndex, tabs, color }: TabsProps) => {
  const [activeIdx, setActiveIdx] = useState(activeIndex || 0);
  useEffect(() => {
    if (activeIndex !== undefined) {
      setActiveIdx(activeIndex);
    }
  }, [activeIndex]);

  const [highlightMargin, setHighlightMargin] = useState('0%');

  const tabCount = tabs.length;
  const tabWidth = `${100 / tabCount}%`;

  const handleClick = (idx: number, cb?: () => void) => {
    setActiveIdx(idx);
    setHighlightMargin(`${(idx / tabCount) * 100}%`);
    cb?.();
  };

  useEffect(() => {
    if (index) {
      setHighlightMargin(`${(index / tabCount) * 100}%`);
    }
  }, [index]);

  useEffect(() => {
    if (activeIdx !== undefined) {
      setHighlightMargin(`${(activeIdx / tabCount) * 100}%`);
      setActiveIdx(activeIdx || 0);
    }
  }, [activeIdx]);

  return (
    <div className={[styles.container, styles[variant], styles[variant.concat(tabCount.toString())]].join(' ')}>
      <div className={styles.tabs}>
        {tabs.map(({ label, onClick, icon, isDisabled, isLocked, tooltip }, idx) => (
          <Tab
            variant={variant}
            key={`tab-${idx}`}
            idx={idx}
            label={label}
            isDisabled={isDisabled}
            isLocked={isLocked}
            tooltip={tooltip}
            isActive={index ? true : activeIdx === idx}
            icon={icon}
            color={color}
            onClick={(id) => handleClick(id, onClick)}
          />
        ))}
      </div>

      <div className={[styles.divider, styles[getColorClass()]].join(' ')}>
        <div
          className={styles.highlight}
          style={{ marginLeft: highlightMargin, width: tabWidth, background: getColor() }}
        />
      </div>
    </div>
  );

  function getColor() {
    return variant === 'color' ? color : '';
  }

  function getColorClass() {
    return variant === 'color' ? 'color' : '';
  }
};

export default Tabs;
